import React, { useState, useEffect, useRef } from 'react';
import { FaCrown, FaTimes } from 'react-icons/fa';
import '../scss/proFeatureModal.scss';
import stripeService from '../services/stripeService';
import { useLocation } from 'react-router-dom';
import api from '../services/apiService';
import { authService } from '../services/authService';

const ProFeatureModal = ({ isOpen, onClose, featureName }) => {
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [paymentError, setPaymentError] = useState(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState(null);
    const location = useLocation();
    const hasLoggedView = useRef(false);
    
    // Check subscription status when modal opens
    useEffect(() => {
        if (isOpen) {
            const checkProStatus = async () => {
                try {
                    const token = sessionStorage.getItem('token');
                    const teamId = sessionStorage.getItem('selected_team_id');
                    const url = teamId 
                        ? `${process.env.REACT_APP_API_URL}/get_subscription_status.php?team_id=${teamId}`
                        : `${process.env.REACT_APP_API_URL}/get_subscription_status.php`;
                    
                    const response = await api.get(url, {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    });
                    
                    if (response.data.success) {
                        setSubscriptionStatus(response.data);
                        
                        // Check if user is pro, manager, or coach - if so, close modal
                        const isProFromAPI = response.data.is_pro === true || response.data.has_active_subscription === true;
                        const isManager = authService.isManager();
                        const isCoach = sessionStorage.getItem('user_type') === 'coach';
                        const isAdmin = authService.isAdmin();
                        
                        if (isProFromAPI || isManager || isCoach || isAdmin) {
                            console.log('User is already Pro/Manager/Coach/Admin, closing modal');
                            onClose();
                            return;
                        }
                    }
                } catch (error) {
                    console.error('Error checking subscription status:', error);
                }
            };
            
            checkProStatus();
        }
    }, [isOpen, onClose]);
    
    useEffect(() => {
        // Check if payment was just completed
        const proPaymentCompleted = sessionStorage.getItem('pro_payment_completed');
        if (proPaymentCompleted === 'true') {
            sessionStorage.removeItem('pro_payment_completed');
            onClose();
        }
    }, [onClose]);

    // Log when modal is opened
    useEffect(() => {
        if (isOpen && !hasLoggedView.current) {
            logProModalView();
            hasLoggedView.current = true;
        } else if (!isOpen) {
            hasLoggedView.current = false;
        }
    }, [isOpen]);

    const logProModalView = async () => {
        try {
            const playerId = sessionStorage.getItem('player_id');
            const teamId = sessionStorage.getItem('selected_team_id');
            const userType = sessionStorage.getItem('user_type');
            const userId = sessionStorage.getItem('user_id');

            if (!teamId) {
                return;
            }

            const actualUserId = userType === 'parent' ? userId : playerId;
            const payload = {
                type: 'pro_modal_view',
                user_id: actualUserId,
                user_type: userType,
                team_id: teamId,
                feature_name: featureName || 'Pro Features',
                source_page: location.pathname
            };

            await api.post('/queue_notification.php', payload);
        } catch (error) {
            console.error('Failed to log Pro modal view:', error);
            console.error('Error details:', error.response?.data || error.message);
        }
    };
    
    if (!isOpen) return null;

    // Check if user is a parent
    const userType = sessionStorage.getItem('user_type');
    const isParent = userType === 'parent' || sessionStorage.getItem('is_parent') === 'true';

    // Conversion-optimized copy based on user type
    const modalCopy = isParent ? {
        headline: "Celebrate Your Child's Football Journey",
        membershipLabel: "FIRSTWHISTLE MEMBERSHIP",
        description: "Get unlimited access to your child's complete football story. See every match, every achievement, and track their progress over time.",
        features: [
            "Match Reports - access all your child's past matches, not just the most recent",
            "Full achievement collection - view every achievement they've earned, not just the latest",
            "See how they compare - view the full leaderboard to see where your child ranks against teammates"
        ],
        cta: 'Join FirstWhistle'
    } : {
        headline: "Unlock Your Complete Football Story",
        description: "Go Pro and get unlimited access to your entire football journey. See every match, every achievement, and track your progress.",
        features: [
            "Compare your stats against teammates - see how you measure up in goals, assists, and more",
            "Full achievement collection - view every achievement you've earned, not just the latest",
            "Full leaderboard access - see where you rank and how you compare to teammates"
        ],
        cta: 'Start My Pro Journey'
    };

    const handleUpgrade = async () => {
        setIsProcessingPayment(true);
        setPaymentError(null);
        
        try {
            const playerId = sessionStorage.getItem('player_id');
            const teamId = sessionStorage.getItem('selected_team_id');
            const teamType = sessionStorage.getItem('selected_team_type');
            const userType = sessionStorage.getItem('user_type');

            if (!teamId) {
                throw new Error('Missing required information');
            }

            // Store the current path as the return URL
            sessionStorage.setItem('pro_return_url', location.pathname);
            
            // Store team name to ensure it's available after Stripe redirect
            const teamName = sessionStorage.getItem('selected_team_name');
            if (teamName) {
                sessionStorage.setItem('pro_team_name', teamName);
            }

            const paymentType = billingCycle === 'monthly' ? 'pro_monthly' : 'pro_annual';
            // Use API URL for thank you page (works for both web and Capacitor apps)
            const apiUrl = process.env.REACT_APP_API_URL || 'https://football.pebbledesigns.co.uk/api';
            const baseUrl = `${apiUrl}/payment-thank-you.php`;
            
            let checkoutOptions;
            
            if (teamType === 'academy' && userType === 'parent') {
                // For academy parents: use parent_id instead of player_id
                const parentId = sessionStorage.getItem('user_id');
                checkoutOptions = {
                    parentId: parseInt(parentId, 10),
                    teamId: parseInt(teamId, 10),
                    amount: billingCycle === 'monthly' ? 4.00 : 52.00,
                    paymentType,
                    isSubscription: billingCycle === 'monthly',
                    subscriberType: 'parent',
                    successUrl: `${baseUrl}?session_id={CHECKOUT_SESSION_ID}&type=${paymentType}`,
                    cancelUrl: `${window.location.origin}${location.pathname}`
                };
            } else {
                // For non-academy teams or players: use existing player_id logic
                checkoutOptions = {
                    playerId: parseInt(playerId, 10),
                    teamId: parseInt(teamId, 10),
                    amount: billingCycle === 'monthly' ? 4.00 : 52.00,
                    paymentType,
                    isSubscription: billingCycle === 'monthly',
                    subscriberType: 'player',
                    successUrl: `${baseUrl}?session_id={CHECKOUT_SESSION_ID}&type=${paymentType}`,
                    cancelUrl: `${window.location.origin}${location.pathname}`
                };
            }

            await stripeService.createAndOpenCheckout(checkoutOptions);
        } catch (error) {
            console.error('Payment error:', error);
            setPaymentError(error.message || 'There was an error initiating the payment. Please try again.');
        } finally {
            setIsProcessingPayment(false);
        }
    };

    return (
        <div className="pro-feature-modal" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <button 
                    className="close-modal-button" 
                    onClick={onClose}
                    aria-label="Close modal"
                >
                    <FaTimes />
                </button>
                <FaCrown className="crown-icon" />
                <h2>{modalCopy.headline}</h2>
                {modalCopy.membershipLabel && (
                    <p className="membership-label">{modalCopy.membershipLabel}</p>
                )}
                <p>
                    {modalCopy.description}
                </p>
                <ul>
                    {modalCopy.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                    ))}
                </ul>

                <div className="pricing-toggle">
                    <button 
                        className={`toggle-button ${billingCycle === 'monthly' ? 'active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setBillingCycle('monthly');
                        }}
                    >
                        Monthly
                    </button>
                    <button 
                        className={`toggle-button annual ${billingCycle === 'annual' ? 'active' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            setBillingCycle('annual');
                        }}
                    >
                        Annual
                        <span className="save-label">Save £12</span>
                    </button>
                </div>

                <div className="pricing-info">
                    {billingCycle === 'monthly' ? (
                        <div className="price-details">
                            <div className="price-container">
                                <span className="price">
                                    <span className="currency-symbol">£</span>4
                                </span>
                            </div>
                            <span className="period">per month</span>
                        </div>
                    ) : (
                        <div className="price-details">
                            <div className="price-container">
                                <span className="price">
                                    <span className="currency-symbol">£</span>52
                                </span>
                            </div>
                            <span className="period">per year - just £1 per week</span>
                        </div>
                    )}
                </div>

                {paymentError && (
                    <div className="error-message">
                        <p>{paymentError}</p>
                        <p>Please try again or contact support if the issue persists.</p>
                    </div>
                )}

                <button 
                    className="upgrade-button" 
                    onClick={handleUpgrade}
                    disabled={isProcessingPayment}
                >
                    {isProcessingPayment 
                        ? 'Processing...' 
                        : modalCopy.cta
                    }
                </button>
                {billingCycle === 'monthly' && (
                    <div className="billing-info">
                        <span>
                            <span className="billing-note">Charged monthly.</span>{' '}
                            <span className="cancel-text" onClick={onClose}>
                                Cancel anytime
                            </span>
                        </span>
                        <span className="payment-note">Payment completed on our website</span>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ProFeatureModal; 